/* App.jsx — نسخه نهایی Dashboard شهری (فارسی، راست‌به‌چپ، Vazir)
   شامل:
   - پروفیل‌های ساعتی مصرف‌کننده‌ها (مسکونی/اداری/رستوران) با منابع معرفی‌شده
   - زیرسیستم‌ها: PVT, CHP, Absorption chiller, Digester, Aquaponics (گلخانه+استخر)
   - بازیافت آب خاکستری و باران، بازیافت حرارت فاضلاب
   - مودال فرمول‌ها، لیست متغیرها و امکان کلیک روی متغیر برای نمایش وابستگی‌ها
   - دکمه پیشنهاد پیکربندی ساده و خودتنظیمی CHP
   توجه: برای دقت تابش حقیقی می‌توان pvlib را وارد کرد؛ این نسخه مدل مفهومی و کامل برای شبیه‌سازی و آزمایش است.
*/

import React, { useMemo, useState } from "react";

/* ==================== استایل و فونت ==================== */
const GlobalStyle = () => (
  <style>{`
    @import url('https://cdn.jsdelivr.net/gh/rastikerdar/vazir-font@v30.1.0/dist/font-face.css');
    :root{
      --bg:#0b1020; --card:#111733; --muted:#9bb0ff; --text:#eaf0ff; --accent:#7aa2ff; --ok:#31c48d; --warn:#f6ad55; --err:#f56565;
      --electric:#4FD1C5; --heat:#F6AD55; --water:#63B3ED; --nutrient:#A3E635; --co2:#F472B6;
    }
    *{box-sizing:border-box}
    html,body,#root{height:100%}
    body{margin:0;background:linear-gradient(180deg,#0b1020,#0e1430 60%,#0b1020);color:var(--text);font-family:'Vazir',system-ui;direction:rtl}
    .container{display:grid;grid-template-columns:420px 1fr;gap:16px;padding:16px}
    .panel{background:var(--card);border:1px solid rgba(255,255,255,0.06);border-radius:16px;padding:12px 14px;box-shadow:0 10px 30px rgba(0,0,0,.35)}
    .title{font-size:18px;margin:8px 0 12px;color:#dbe5ff;display:flex;align-items:center;gap:8px}
    .sub{font-size:13px;color:#b8c7ff}
    .row{display:flex;align-items:center;gap:8px;margin:8px 0}
    .col{display:flex;flex-direction:column;gap:6px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
    .grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px}
    .slider{width:100%}
    input[type=range]{width:100%}
    .badge{display:inline-block;font-size:11px;padding:3px 8px;border-radius:999px;background:#233056;color:#b7c5ff}
    .kpi{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:8px}
    .kpi .card{background:#17204a;border:1px solid rgba(255,255,255,.05);padding:10px;border-radius:12px}
    .kpi .val{font-size:18px;color:#fff}
    .kpi .lbl{font-size:12px;color:#b7c5ff}
    .svgwrap{height:560px;background:#0f1638;border-radius:16px;border:1px solid rgba(255,255,255,.06);position:relative;padding:8px}
    .legend{display:flex;gap:10px;flex-wrap:wrap;margin-top:8px}
    .legend .item{display:flex;align-items:center;gap:6px;font-size:12px;color:#cfe2ff}
    .dot{width:18px;height:4px;border-radius:2px}
    .pill{background:#17204a;border:1px solid rgba(255,255,255,.08);padding:6px 10px;border-radius:999px;font-size:12px;color:#cfe2ff}
    .btn{background:#22306a;color:#e7edff;border:1px solid rgba(255,255,255,.08);padding:8px 12px;border-radius:12px;cursor:pointer}
    .btn:hover{background:#263574}
    .modal{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(8,12,28,.75);z-index:60}
    .modal .box{max-width:960px;width:96%;background:#0f1638;border:1px solid rgba(255,255,255,.08);border-radius:16px;padding:16px}
    .muted{color:#9fb4ff}
    .chart{height:170px;background:#0f1638;border:1px solid rgba(255,255,255,.06);border-radius:12px;position:relative;padding:6px}
    .sep{height:1px;background:linear-gradient(90deg,transparent,#2a376d,transparent);margin:10px 0}
    details summary{cursor:pointer;color:#cfe2ff}
    .var-list{max-height:320px;overflow:auto;padding:8px;border-radius:8px;background:#0b1230;border:1px solid rgba(255,255,255,0.03)}
    .var-item{padding:8px;border-radius:8px;margin:6px 0;background:#0f1730;border:1px solid rgba(255,255,255,0.02);cursor:pointer}
    .link-dot{display:inline-block;width:10px;height:10px;border-radius:50%;margin-left:6px}
    @media(max-width:1100px){.container{grid-template-columns:1fr}}
  `}</style>
);

/* ==================== ابزارک‌ها ==================== */
const Field = ({ label, children, hint }) => (
  <div className="col">
    <div className="row" style={{justifyContent:'space-between'}}>
      <div className="sub">{label}</div>
      {hint && <span className="badge" title={hint}>i</span>}
    </div>
    {children}
  </div>
);

const Slider = ({ value, onChange, min=0, max=100, step=1, postfix="" }) => (
  <div className="row">
    <input type="range" className="slider" value={value} min={min} max={max} step={step}
           onChange={(e)=>onChange(Number(e.target.value))} />
    <input type="number" style={{width:100}} value={value}
           onChange={(e)=>onChange(Number(e.target.value))} />
    <span className="pill">{postfix}</span>
  </div>
);

const clamp = (x,min,max)=> Math.max(min, Math.min(max,x));

/* ==================== پروفیل‌های ساعتی و پارامترها (مستند/منبع) ==================== */
/* پروفیل‌ها نرمال‌شده‌اند؛ منابع: NREL DOE commercial reference, ASHRAE, EPA/REUWS, FAO56, Goddek et al. */
const SCHED = {
  res: {
    el:  [0.25,0.22,0.20,0.20,0.22,0.30,0.40,0.55,0.70,0.60,0.55,0.50,0.45,0.45,0.50,0.60,0.80,0.95,1.00,0.95,0.80,0.65,0.45,0.35],
    dhw:[0.02,0.02,0.02,0.02,0.03,0.08,0.12,0.10,0.07,0.05,0.04,0.04,0.04,0.04,0.05,0.06,0.08,0.12,0.15,0.12,0.08,0.05,0.03,0.02],
    occ:[0.80,0.78,0.76,0.75,0.78,0.85,0.90,0.85,0.80,0.75,0.72,0.72,0.75,0.78,0.82,0.88,0.95,1.00,1.00,0.98,0.92,0.88,0.85,0.82]
  },
  off: {
    el:  [0.10,0.08,0.08,0.08,0.10,0.40,0.70,0.90,1.00,1.00,1.00,1.00,0.95,0.90,0.85,0.80,0.70,0.50,0.30,0.20,0.15,0.12,0.10,0.10],
    dhw:[0.02,0.02,0.02,0.02,0.03,0.06,0.08,0.10,0.12,0.12,0.10,0.10,0.08,0.06,0.05,0.05,0.05,0.05,0.04,0.03,0.03,0.02,0.02,0.02],
    occ:[0.05,0.05,0.05,0.05,0.10,0.40,0.70,0.90,1.00,1.00,1.00,1.00,0.95,0.90,0.85,0.80,0.60,0.30,0.10,0.05,0.05,0.05,0.05,0.05]
  },
  rst: {
    el:  [0.10,0.08,0.08,0.08,0.10,0.20,0.30,0.50,0.70,0.80,0.90,1.00,0.90,0.80,0.70,0.70,0.80,1.00,1.00,0.90,0.70,0.50,0.30,0.20],
    dhw:[0.02,0.02,0.02,0.02,0.03,0.05,0.08,0.12,0.15,0.20,0.22,0.25,0.22,0.18,0.15,0.18,0.25,0.30,0.28,0.20,0.12,0.08,0.05,0.03],
    occ:[0.05,0.05,0.05,0.05,0.10,0.20,0.30,0.50,0.70,0.85,1.00,1.00,0.95,0.85,0.75,0.80,0.95,1.00,0.95,0.80,0.60,0.40,0.25,0.15]
  }
};

const INTENSITY = {
  res: { el_kWh_ppd: 3.5,  dhw_L_ppd: 55,  heat_kWh_ppd: 6.0, cool_kWh_ppd: 5.0 },
  off: { el_kWh_m2d: 0.9, dhw_L_ppd: 6,   heat_kWh_m2d: 0.6, cool_kWh_m2d: 0.8 },
  rst: { el_kWh_m2d: 3.5, dhw_L_m2d: 25,  heat_kWh_m2d: 1.2, cool_kWh_m2d: 2.0 }
};

const CLIM_FACT = {
  summer: { heat: 0.4, cool: 1.0 },
  mild:   { heat: 0.7, cool: 0.7 },
  winter: { heat: 1.0, cool: 0.3 }
};

/* ==================== متغیرها/فرمول‌ها/وابستگی‌ها (برای نمایش) ==================== */
/* هر متغیر یک آبجکت با واحد، توضیح کوتاه و فهرست ارتباط‌ها دارد */
const VARIABLES = {
  // نمونه از متغیرهای کلیدی با روابط
  "A_PVT": { unit:"m²", desc:"مساحت پانل PVT", links:["P_el","Q_th","roof_area_competition"] },
  "eta_PV": { unit:"-", desc:"بازده الکتریکی PV", links:["P_el"] },
  "eta_th": { unit:"-", desc:"بازده حرارتی PVT", links:["Q_th","ABS"] },

  "P_el": { unit:"kW", desc:"توان الکتریکی تولیدی", links:["BLD","CHP","grid"] },
  "Q_th": { unit:"kW", desc:"حرارت مفید خروجی", links:["ABS","BLD","GH"] },

  "CHP_Pnom": { unit:"kW", desc:"توان نامی الکتریکی CHP", links:["P_el","Q_th","biogas_use"] },
  "eta_el_chp": { unit:"-", desc:"بازده الکتریکی CHP", links:["CHP_Pnom","fuel_need"] },

  "V_grey": { unit:"L/day", desc:"حجم آب خاکستری تولیدی", links:["V_reuse","GH","HX"] },
  "eta_grey": { unit:"-", desc:"راندمان تصفیه آب خاکستری", links:["V_reuse"] },
  "V_reuse": { unit:"L/day", desc:"آب خاکستری قابل استفاده", links:["GH","W_net"] },

  "A_GH": { unit:"m²", desc:"سطح اشغال گلخانه (افقی)", links:["Prod","W_grow","roof_area_competition"] },
  "layers_GH": { unit:"-", desc:"تعداد طبقات گلخانه", links:["Prod","W_grow"] },
  "P_light_density": { unit:"W/m²", desc:"چگالی توان نور (LED/HPS)", links:["P_light","Prod"] },
  "P_light": { unit:"kW", desc:"توان مصرفی روشنایی گلخانه (ساعتی)", links:["PelLoad","Prod"] },
  "Prod": { unit:"kg/day", desc:"تولید روزانه محصول", links:["VS_plant","food_target"] },

  "V_tank": { unit:"m³", desc:"حجم استخر ماهی", links:["B_fish","feed"] },
  "rho_fish": { unit:"kg/m³", desc:"چگالی ذخیره ماهی", links:["B_fish"] },
  "B_fish": { unit:"kg", desc:"بیوماس ماهی", links:["feed","VS_fish"] },
  "FDR": { unit:"%/day", desc:"نرخ روزانه خوراک (فراکشن از بیوماس)", links:["feed","O2_need"] },
  "feed": { unit:"kg/day", desc:"خوراک روزانه", links:["O2_need","VS_fish"] },
  "O2_need": { unit:"kgO2/day", desc:"اکسیژن موردنیاز", links:["aeration_energy"] },

  "VS_plant": { unit:"kg/day", desc:"مواد قابل تخمیر از ضایعات گیاهی", links:["digester"] },
  "VS_fish": { unit:"kg/day", desc:"مواد قابل تخمیر از فضولات ماهی", links:["digester"] },
  "VS_base": { unit:"kg/day", desc:"پسماند آلی پایه وارد هاضم", links:["digester"] },

  "Y_CH4": { unit:"m³/kgVS", desc:"بازده تولید متان از VS", links:["V_CH4"] },
  "V_CH4": { unit:"m³/day", desc:"حجم متان تولیدی روزانه", links:["biogas_energy","CHP_fuel"] },
  "E_biogas": { unit:"kWh/day", desc:"انرژی محتوی بیوگاز", links:["CHP_fuel"] },

  "roof_area_competition": { unit:"m²", desc:"رقابت بین PVT و گلخانه برای استفاده از سقف/فضای قابل‌استفاده", links:["A_PVT","A_GH"] },

  // ... می‌توان بقیه متغیرها را اضافه کرد
};

/* برای هر زیرسیستم فرمول‌ها نیز به صورت متنی آماده است */
const FORMULAS = {
  PVT:[
    { f:"P_el(h)=eta_PV * A_PVT * I(h)", desc:"توان الکتریکی ساعتی" },
    { f:"Q_th(h)=eta_th * A_PVT * I(h)", desc:"حرارت بازیافتی ساعتی" }
  ],
  CHP:[
    { f:"P_el = eta_el * Q_fuel", desc:"برق خروجی" },
    { f:"Q_th = eta_th * Q_fuel", desc:"حرارت خروجی" },
    { f:"Q_fuel = E_biogas + E_citygas", desc:"ترکیب سوخت" }
  ],
  AQUA:[
    { f:"B_fish = rho_fish * V_tank", desc:"بیوماس ماهی" },
    { f:"feed = FDR * B_fish", desc:"خوراک روزانه" },
    { f:"O2 ≈ 0.5 * feed", desc:"اکسیژن تقریبی موردنیاز" },
    { f:"Prod = Y_base * A_GH * layers_GH * F_light", desc:"تولید گیاهی روزانه" }
  ],
  DIG:[
    { f:"V_CH4 = Y_CH4 * (VS_base + VS_fish + VS_plant)", desc:"متان روزانه" },
    { f:"E_biogas = LHV_CH4 * V_CH4", desc:"انرژی بیوگاز" }
  ],
  WATER:[
    { f:"V_reuse = eta_grey * V_grey", desc:"آب خاکستری قابل استفاده" },
    { f:"V_rain = C_r * P_mm * A", desc:"آب باران روزانه (L ≈ mm*m2)" },
    { f:"Q_rec = eta_hx * m * cp * dT", desc:"حرارت بازیافتی از فاضلاب" }
  ]
};

/* ==================== اقلیم ساده ==================== */
const CLIMATE = {
  summer: { name:"تابستان گرم", gha: 800, T_amb: 36, RH: 0.18, rain_mm: 0.5 },
  winter: { name:"زمستان سرد", gha: 350, T_amb: 6,  RH: 0.45, rain_mm: 2.8 },
  mild:   { name:"بینابینی",   gha: 550, T_amb: 18, RH: 0.35, rain_mm: 1.2 },
};

/* ==================== پارامترها و شبیه‌ساز ==================== */
const LIMITS = { chp_el_max_kw: 300, chp_heat_max_kw: 400, grid_max_kw: 1000, gas_max_kw: 1000 };

/* تابعی که بارهای ساعتی را از کاربری‌ها می‌سازد (مبتنی بر SCHED & INTENSITY) */
function buildHourlyLoads({ scenario, floorsRes, unitsPerFloorRes, peoplePerUnit, floorsOff, floorsRest }){
  const hours = [...Array(24).keys()];

  // تقریب ابعاد
  const people_res = floorsRes * unitsPerFloorRes * peoplePerUnit;
  const units_off  = floorsOff * 4;     // فرض 4 واحد اداری/طبقه
  const people_off = units_off * 8;     // تقریب 8 نفر/واحد اداری
  const area_off   = units_off * 80;    // m2/واحد اداری

  const units_rst  = floorsRest * 4;
  const area_rst   = units_rst * 120;

  // روزانه (اسکالر)
  const E_res_day = INTENSITY.res.el_kWh_ppd * people_res;
  const W_res_day = INTENSITY.res.dhw_L_ppd * people_res;
  const H_res_day = INTENSITY.res.heat_kWh_ppd * people_res * CLIM_FACT[scenario].heat;
  const C_res_day = INTENSITY.res.cool_kWh_ppd * people_res * CLIM_FACT[scenario].cool;

  const E_off_day = INTENSITY.off.el_kWh_m2d * area_off;
  const W_off_day = INTENSITY.off.dhw_L_ppd * people_off;
  const H_off_day = INTENSITY.off.heat_kWh_m2d * area_off * CLIM_FACT[scenario].heat;
  const C_off_day = INTENSITY.off.cool_kWh_m2d * area_off * CLIM_FACT[scenario].cool;

  const E_rst_day = INTENSITY.rst.el_kWh_m2d * area_rst;
  const W_rst_day = INTENSITY.rst.dhw_L_m2d * area_rst;
  const H_rst_day = INTENSITY.rst.heat_kWh_m2d * area_rst * CLIM_FACT[scenario].heat;
  const C_rst_day = INTENSITY.rst.cool_kWh_m2d * area_rst * CLIM_FACT[scenario].cool;

  // نرمال‌سازی به ساعتی
  const scaleToHourly = (sched, total)=>{
    const s = sched.reduce((a,b)=>a+b,0);
    const f = s>0 ? total / s : 0;
    return sched.map(v=> v * f);
  };

  const Pel_res   = scaleToHourly(SCHED.res.el,  E_res_day);
  const DHW_res   = scaleToHourly(SCHED.res.dhw, W_res_day);
  const Heat_res  = scaleToHourly(SCHED.res.occ, H_res_day);
  const Cool_res  = scaleToHourly(SCHED.res.occ, C_res_day);

  const Pel_off   = scaleToHourly(SCHED.off.el,  E_off_day);
  const DHW_off   = scaleToHourly(SCHED.off.dhw, W_off_day);
  const Heat_off  = scaleToHourly(SCHED.off.occ, H_off_day);
  const Cool_off  = scaleToHourly(SCHED.off.occ, C_off_day);

  const Pel_rst   = scaleToHourly(SCHED.rst.el,  E_rst_day);
  const DHW_rst   = scaleToHourly(SCHED.rst.dhw, W_rst_day);
  const Heat_rst  = scaleToHourly(SCHED.rst.occ, H_rst_day);
  const Cool_rst  = scaleToHourly(SCHED.rst.occ, C_rst_day);

  const Pel_kw = hours.map(h=> Pel_res[h] + Pel_off[h] + Pel_rst[h]); // kWh -> kW
  const Qheat_kw = hours.map(h=> Heat_res[h] + Heat_off[h] + Heat_rst[h]);
  const Qcool_kw = hours.map(h=> Cool_res[h] + Cool_off[h] + Cool_rst[h]);
  const DHW_Lph = hours.map(h=> DHW_res[h] + DHW_off[h] + DHW_rst[h]);

  return { hours, Pel_kw, Qheat_kw, Qcool_kw, DHW_Lph, meta:{people_res, people_off, area_off, area_rst} };
}

/* شبیه‌ساز ساعتی — بازگرداندن همهٔ مقادیر برای UI */
function useSimulation(params){
  const {
    scenario,
    roofAreaMax, targetSelfEl, targetSelfHeat, targetVeg_kg_day,
    floorsRes, unitsPerFloorRes, peoplePerUnit, floorsOff, floorsRest,
    pvtArea, pvEff, pvtThEff,
    chpElEff, chpThEff, chpNomElKw, chpAuto,
    digesterVSkgd_base, methaneYield, recoverHXeta,
    absCOP,
    ghArea, ghLayers, lightType, lightHours, autoLighting,
    tankVol_m3, fishDensity_kgm3, fdr_percent,
    rainCoeff, greyEta
  } = params;

  const clim = CLIMATE[scenario];
  const hours = [...Array(24).keys()];

  // بارهای ساعتی ساختمان
  const loads = buildHourlyLoads({ scenario, floorsRes, unitsPerFloorRes, peoplePerUnit, floorsOff, floorsRest });
  const PelBase = loads.Pel_kw.slice();
  const QheatBase = loads.Qheat_kw.slice();
  const QcoolBase = loads.Qcool_kw.slice();
  const DHW_Lph = loads.DHW_Lph;
  const peopleTotal = floorsRes * unitsPerFloorRes * peoplePerUnit + loads.meta.people_off + floorsRest * 4 * 8;

  // تولید PVT ساعتی (تابش سینوسی ساده)
  const gha = clim.gha;
  const Gt = hours.map(h=> Math.max(0, Math.sin(Math.PI*(h-6)/12)) * gha );
  const PelPVT = Gt.map(g=> g * pvtArea * pvEff / 1000);
  const QthPVT = Gt.map(g=> g * pvtArea * pvtThEff / 1000);
  const EelPVT_day_kWh = PelPVT.reduce((a,b)=>a+b,0);
  const QthPVT_day_kWh = QthPVT.reduce((a,b)=>a+b,0);

  // گلخانه (ساعتی)
  const P_LIGHT_Wm2 = (lightType==='LED'? 120: 230);
  const F_light = (lightType==='LED'? 1.10: 1.00);
  const Y_base = 3.0/100; // kg/day per 100 m2
  const Prod_kg_day = Y_base * ghArea * ghLayers * F_light;

  const lightSched = hours.map(h=>{
    const on = Math.round(lightHours);
    const start = 6;
    return (h>=start && h<start+on)? 1 : 0;
  });
  const P_light_kW = lightSched.map(s=> (ghArea * P_LIGHT_Wm2 * s)/1000);
  const E_light_kWh_day = P_light_kW.reduce((a,b)=>a+b,0);

  // آب گلخانه (FAO-56 ساده)
  const ETc_L_m2_day = scenario==='summer'? 6.0 : scenario==='winter'? 2.5 : 4.0;
  const W_grow_L_day = ETc_L_m2_day * ghArea * ghLayers;
  const W_grow_Lph = hours.map(()=> W_grow_L_day/24);

  // آکواپونیک — استخر ماهی
  const B_fish_kg = tankVol_m3 * fishDensity_kgm3;
  const feed_kg_day = (fdr_percent/100) * B_fish_kg;
  const O2_d_kg = 0.5 * feed_kg_day;
  const E_aer_kWh_day = 1.0 * O2_d_kg;
  const P_aer_kW = hours.map(()=> E_aer_kWh_day/24);

  // VS ورودی هاضم
  const VS_fish = 0.3 * feed_kg_day;
  const VS_plant = 0.15 * Prod_kg_day;
  const digesterVSkgd = digesterVSkgd_base + VS_fish + VS_plant;
  const V_CH4_m3_day = digesterVSkgd * methaneYield;
  const LHV_CH4_kWh_m3 = 9.97;
  const E_fuel_biogas_kWh_day = V_CH4_m3_day * LHV_CH4_kWh_m3;

  // آب خاکستری و باران
  const V_grey_L_day = DHW_Lph.reduce((a,b)=>a+b,0);
  const V_reuse_L_day = V_grey_L_day * greyEta;
  const V_rain_L_day = clim.rain_mm * rainCoeff * (ghArea + pvtArea);
  const V_reuse_Lph = DHW_Lph.map(v=> v * greyEta);
  const V_rain_Lph = hours.map(()=> V_rain_L_day/24);

  // ریکاوری حرارت فاضلاب
  const m_dot_kg_s = (V_grey_L_day/24)/3.6;
  const cp = 4.18; const dT = scenario==='winter'? 15 : 8;
  const Qrec_kW = recoverHXeta * m_dot_kg_s * cp * dT;

  // CHP — خودتنظیمی
  let chpElKw_set = chpNomElKw;
  if(chpAuto){
    const PelLoad_plus_GH = hours.map(h=> PelBase[h] + P_light_kW[h] + P_aer_kW[h]);
    const avgDef = hours.map(h=> (PelPVT[h] + chpNomElKw) - PelLoad_plus_GH[h]).reduce((a,b)=>a+b,0)/24;
    chpElKw_set = clamp(chpNomElKw - avgDef, 0, LIMITS.chp_el_max_kw);
  }
  const chpEl_profile = hours.map(()=> chpElKw_set);
  const chpHeatKw = chpElKw_set * (chpThEff/chpElEff);

  // برق: تراز ساعتی
  const PelLoad_total = hours.map(h=> PelBase[h] + P_light_kW[h] + P_aer_kW[h]);
  const PelBalance = hours.map(h=> PelPVT[h] + chpEl_profile[h] - PelLoad_total[h]);
  const gridImport_kWh = PelBalance.map(v=> v<0? -v: 0).reduce((a,b)=>a+b,0);
  const gridExport_kWh = PelBalance.map(v=> v>0? v: 0).reduce((a,b)=>a+b,0);

  // حرارت: تراز ساعتی
  const QheatAvail = hours.map((h)=> chpHeatKw + QthPVT[h] + Qrec_kW);
  const QheatDef = hours.map((h)=> Math.max(0, QheatBase[h] - QheatAvail[h]));
  const QheatAvail_kW = QheatAvail.reduce((a,b)=>a+b,0)/24;
  const QheatDef_kW = QheatDef.reduce((a,b)=>a+b,0)/24;

  // سرمایش جذبی از حرارت
  const Qcold_from_abs = hours.map(h=> absCOP*(QthPVT[h] + chpHeatKw));
  const QcoolDef = hours.map(h=> Math.max(0, QcoolBase[h] - Qcold_from_abs[h]));
  const QcoolDef_kW = QcoolDef.reduce((a,b)=>a+b,0)/24;

  // آب خالص گلیخانه
  const W_net_Lph = hours.map(h=> Math.max(0, W_grow_Lph[h] - (V_reuse_Lph[h] + V_rain_Lph[h])));
  const W_net_L_day = W_net_Lph.reduce((a,b)=>a+b,0);

  // سوخت CHP (اولویت بیوگاز)
  const fuelNeed_kWh_day = chpElKw_set*24 / chpElEff;
  const biogas_used = Math.min(E_fuel_biogas_kWh_day, fuelNeed_kWh_day);
  const gas_city_kWh_day = Math.max(0, fuelNeed_kWh_day - biogas_used);

  return {
    hours, climate:clim,
    PelPVT, QthPVT, EelPVT_day_kWh, QthPVT_day_kWh,
    PelLoad: PelLoad_total, QcoolLoad: QcoolBase, QheatLoad: QheatBase,
    PelBalance_auto: PelBalance, gridImport_kWh, gridExport_kWh,
    chpElKw_auto: chpElKw_set, chpHeatKw, QheatAvail_kW, QheatDef_kW, QcoolDef_kW,
    V_grey_L_day, V_reuse_L_day, V_rain_L_day, W_grow_L_day, W_net_L_day,
    Prod_kg_day, E_light_kWh_day, B_fish_kg, feed_kg_day, O2_d_kg, E_aer_kWh_day,
    biogas:{ V_CH4_m3_day, E_fuel_biogas_kWh_day, biogas_used }, fuel:{ gas_city_kWh_day },
    Qrec_kW,
  };
}

/* ==================== چیدمان گراف و کامپوننت‌‌های UI ==================== */
const NODE_LAYOUT = {
  PVT:{x:150,y:120}, CHP:{x:150,y:360}, ABS:{x:460,y:120}, BLD:{x:820,y:120}, GH:{x:820,y:360}, DIG:{x:460,y:360}, WTR:{x:460,y:230}
};

const NodeCard = ({ id, label, onClick, kpi }) => (
  <g onClick={onClick} style={{cursor:'pointer'}}>
    <rect x={NODE_LAYOUT[id].x-80} y={NODE_LAYOUT[id].y-30} rx={14} ry={14} width={160} height={64}
          fill="#16224e" stroke="#2a3a78" />
    <text x={NODE_LAYOUT[id].x} y={NODE_LAYOUT[id].y-4} textAnchor="middle" fill="#eaf0ff" fontSize="14">{label}</text>
    <text x={NODE_LAYOUT[id].x} y={NODE_LAYOUT[id].y+18} textAnchor="middle" fill="#a9baff" fontSize="11">{kpi}</text>
  </g>
);

const Edge = ({ from, to, value, kind }) => {
  const a = NODE_LAYOUT[from], b = NODE_LAYOUT[to];
  const color = { electric:'var(--electric)', heat:'var(--heat)', water:'var(--water)', nutrient:'var(--nutrient)', co2:'var(--co2)' }[kind] || '#888';
  const w = clamp(Math.abs(value)/6, 1.2, 12);
  const path = `M ${a.x} ${a.y} C ${a.x+110} ${a.y}, ${b.x-110} ${b.y}, ${b.x} ${b.y}`;
  return <path d={path} stroke={color} strokeWidth={w} fill="none" opacity={0.9}/>;
};

/* مودال عمومی */
function Modal({ open, onClose, title, children }){
  if(!open) return null;
  return (
    <div className="modal" onClick={onClose}>
      <div className="box" onClick={(e)=>e.stopPropagation()}>
        <div className="row" style={{justifyContent:'space-between',alignItems:'center'}}>
          <div className="title">{title}</div>
          <button className="btn" onClick={onClose}>بستن</button>
        </div>
        <div className="sep"/>
        {children}
      </div>
    </div>
  );
}

/* ==================== کامپوننت اصلی App ==================== */
export default function App(){
  const [scenario, setScenario] = useState('mild');

  // مصرف‌کننده‌ها و اهداف
  const [floorsRes, setFloorsRes] = useState(5);
  const [unitsPerFloorRes, setUnitsPerFloorRes] = useState(4);
  const [peoplePerUnit, setPeoplePerUnit] = useState(4);
  const [floorsOff, setFloorsOff] = useState(2);
  const [floorsRest, setFloorsRest] = useState(1);

  const [roofAreaMax, setRoofAreaMax] = useState(300);
  const [targetSelfEl, setTargetSelfEl] = useState(0.7);
  const [targetSelfHeat, setTargetSelfHeat] = useState(0.6);
  const [targetVeg_kg_day, setTargetVeg_kg_day] = useState(20);

  // تولید/بازیافت
  const [pvtArea, setPvtArea] = useState(150);
  const [pvEff, setPvEff] = useState(0.18);
  const [pvtThEff, setPvtThEff] = useState(0.4);

  const [chpElEff, setChpElEff] = useState(0.33);
  const [chpThEff, setChpThEff] = useState(0.48);
  const [chpNomElKw, setChpNomElKw] = useState(60);
  const [chpAuto, setChpAuto] = useState(true);

  const [digesterVSkgd_base, setDigesterVSkgd_base] = useState(160);
  const [methaneYield, setMethaneYield] = useState(0.35);
  const [recoverHXeta, setRecoverHXeta] = useState(0.45);

  const [absCOP, setAbsCOP] = useState(0.7);

  const [ghArea, setGhArea] = useState(140);
  const [ghLayers, setGhLayers] = useState(3);
  const [lightType, setLightType] = useState('LED');
  const [lightHours, setLightHours] = useState(14);
  const [autoLighting, setAutoLighting] = useState(true);

  const [tankVol_m3, setTankVol_m3] = useState(15);
  const [fishDensity_kgm3, setFishDensity_kgm3] = useState(25);
  const [fdr_percent, setFdr_percent] = useState(1.6);

  const [rainCoeff, setRainCoeff] = useState(0.8);
  const [greyEta, setGreyEta] = useState(0.6);

  const [showModal, setShowModal] = useState(null); // PVT|CHP|ABS|DIG|WATER|AQUA|BLD
  const [varDetail, setVarDetail] = useState(null); // متغیر انتخاب‌شده برای نمایش وابستگی‌ها

  const setpoints = {}; // قبلاً تبدیل شد؛ الان buildHourlyLoads استفاده می‌شود

  /* فراخوانی شبیه‌ساز */
  const sim = useSimulation({
    scenario, roofAreaMax, targetSelfEl, targetSelfHeat, targetVeg_kg_day,
    floorsRes, unitsPerFloorRes, peoplePerUnit, floorsOff, floorsRest,
    pvtArea, pvEff, pvtThEff,
    chpElEff, chpThEff, chpNomElKw, chpAuto,
    digesterVSkgd_base, methaneYield, recoverHXeta,
    absCOP,
    ghArea, ghLayers, lightType, lightHours, autoLighting,
    tankVol_m3, fishDensity_kgm3, fdr_percent,
    rainCoeff, greyEta
  });

  /* پیشنهاد پیکربندی ساده (heuristic) */
  function suggestConfig(){
    const F_light = (lightType==='LED'?1.10:1.0);
    const Y_base = 3.0/100;
    const needed_A_layers = targetVeg_kg_day / (Y_base * F_light);
    let newGhArea = clamp(needed_A_layers/ghLayers, 50, roofAreaMax);

    const Pel_day_kWh = sim.PelLoad.reduce((a,b)=>a+b,0);
    const target_el_kWh = targetSelfEl * Pel_day_kWh;
    const spec_kWh_m2 = (sim.EelPVT_day_kWh>0 && pvtArea>0)? (sim.EelPVT_day_kWh/pvtArea) : (CLIMATE[scenario].gha*0.45*12/1000);
    let newPvtArea = clamp(target_el_kWh*0.55 / spec_kWh_m2, 0, roofAreaMax - newGhArea);

    const rem_el_kWh = Math.max(0, target_el_kWh - newPvtArea*spec_kWh_m2);
    let newChpElKw = clamp(rem_el_kWh/24, 0, LIMITS.chp_el_max_kw);

    let newGrey = greyEta;
    if(sim.W_net_L_day>0) newGrey = clamp(greyEta + 0.1, 0.3, 0.9);

    setGhArea(Math.round(newGhArea));
    setPvtArea(Math.round(newPvtArea));
    setChpNomElKw(Math.round(newChpElKw));
    setGreyEta(parseFloat(newGrey.toFixed(2)));
  }

  /* گراف لبه‌ها بر اساس مقدارها (نمادین) */
  const edges = useMemo(()=>[
    {from:'PVT', to:'BLD', value: sim.EelPVT_day_kWh/24, kind:'electric'},
    {from:'CHP', to:'BLD', value: sim.chpElKw_auto, kind:'electric'},
    {from:'PVT', to:'ABS', value: (sim.QthPVT_day_kWh/24), kind:'heat'},
    {from:'CHP', to:'ABS', value: sim.chpHeatKw, kind:'heat'},
    {from:'WTR', to:'BLD', value: sim.Qrec_kW, kind:'heat'},
    {from:'WTR', to:'GH',  value: (sim.V_reuse_L_day/24 + sim.V_rain_L_day/24), kind:'water'},
    {from:'DIG', to:'GH',  value: sim.Prod_kg_day, kind:'nutrient'},
    {from:'DIG', to:'CHP', value: sim.biogas.E_fuel_biogas_kWh_day/24, kind:'co2'},
  ],[sim]);

  /* نمایش وابستگی‌های متغیرها */
  function VarDetailBox({ varId }){
    if(!varId) return <div className="muted">یک متغیر انتخاب کنید تا وابستگی‌ها نمایش داده شود.</div>;
    const v = VARIABLES[varId];
    if(!v) return <div className="muted">متغیر تعریف نشده.</div>;
    return (
      <div>
        <div style={{marginBottom:8}} className="sub">متغیر: <b>{varId}</b> — <span className="muted">{v.unit}</span></div>
        <div style={{marginBottom:8}} className="muted">{v.desc}</div>
        <div className="sub">وابستگی‌ها (این متغیر به این آیتم‌ها مرتبط است):</div>
        <div className="var-list" style={{marginTop:8}}>
          {v.links.map((l,i)=>(
            <div key={i} className="var-item" onClick={()=>setVarDetail(l)}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div><b>{l}</b></div>
                <div style={{display:'flex',gap:8}}>
                  <span style={{background: '#4FD1C5'}} className="link-dot" title="الکتریکی"></span>
                </div>
              </div>
              <div style={{fontSize:12,color:'#9fb4ff',marginTop:6}}>
                { (FORMULAS.PVT.find(f=>f.f.includes(l))||FORMULAS.AQUA.find(f=>f.f.includes(l))||{desc:''}).desc || '' }
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <GlobalStyle/>
      <div className="container">
        {/* ستون چپ */}
        <div className="panel">
          <div className="title">۱) مشخصات مصرف‌کننده‌ها و اهداف</div>

          <div className="row" style={{gap:6,flexWrap:'wrap'}}>
            {Object.entries(CLIMATE).map(([k,v])=> (
              <button key={k} className="pill" onClick={()=>setScenario(k)} style={{background: scenario===k? '#2a3a78':'#17204a'}}>{v.name}</button>
            ))}
            <span className="badge">Tₐ: {CLIMATE[scenario].T_amb}°C</span>
            <span className="badge">بارش: {CLIMATE[scenario].rain_mm} mm/d</span>
          </div>

          <div className="grid3" style={{marginTop:8}}>
            <Field label="طبقات مسکونی"><Slider value={floorsRes} onChange={setFloorsRes} min={1} max={8} step={1} postfix="floor"/></Field>
            <Field label="واحد/طبقه"><Slider value={unitsPerFloorRes} onChange={setUnitsPerFloorRes} min={1} max={6} step={1} postfix="unit"/></Field>
            <Field label="نفر/واحد"><Slider value={peoplePerUnit} onChange={setPeoplePerUnit} min={1} max={6} step={1} postfix="person"/></Field>
          </div>

          <div className="grid3">
            <Field label="طبقات اداری"><Slider value={floorsOff} onChange={setFloorsOff} min={0} max={6} step={1} postfix="floor"/></Field>
            <Field label="طبقات رستوران"><Slider value={floorsRest} onChange={setFloorsRest} min={0} max={3} step={1} postfix="floor"/></Field>
            <Field label="سقف قابل‌استفاده (PVT+گلخانه)"><Slider value={roofAreaMax} onChange={setRoofAreaMax} min={50} max={800} step={10} postfix="m²"/></Field>
          </div>

          <div className="grid3" style={{marginTop:8}}>
            <Field label="هدف خودکفایی برق"><Slider value={targetSelfEl} onChange={setTargetSelfEl} min={0.3} max={1.0} step={0.05} postfix="fraction"/></Field>
            <Field label="هدف خودکفایی حرارت"><Slider value={targetSelfHeat} onChange={setTargetSelfHeat} min={0.3} max={1.0} step={0.05} postfix="fraction"/></Field>
            <Field label="هدف تولید (kg/day)"><Slider value={targetVeg_kg_day} onChange={setTargetVeg_kg_day} min={0} max={300} step={5} postfix="kg/day"/></Field>
          </div>

          <div className="row" style={{justifyContent:'flex-end',gap:10,marginTop:6}}>
            <button className="btn" onClick={suggestConfig}>پیشنهاد پیکربندی (ساده)</button>
          </div>

          <div className="sep"/>

          <details>
            <summary>تنظیمات تولید و زیرسیستم‌ها (پیشرفته)</summary>

            <div style={{marginTop:10}} className="title">PVT</div>
            <Field label="مساحت PVT"><Slider value={pvtArea} onChange={setPvtArea} min={0} max={roofAreaMax} step={5} postfix="m²"/></Field>
            <div className="grid">
              <Field label="η_PV"><Slider value={pvEff} onChange={setPvEff} min={0.12} max={0.28} step={0.01} postfix="-"/></Field>
              <Field label="η_th"><Slider value={pvtThEff} onChange={setPvtThEff} min={0.2} max={0.6} step={0.02} postfix="-"/></Field>
            </div>

            <div className="title" style={{marginTop:8}}>CHP</div>
            <div className="grid3">
              <Field label="P_el نامی CHP"><Slider value={chpNomElKw} onChange={setChpNomElKw} min={0} max={LIMITS.chp_el_max_kw} step={5} postfix="kW"/></Field>
              <Field label="η_el"><Slider value={chpElEff} onChange={setChpElEff} min={0.25} max={0.42} step={0.01} postfix="-"/></Field>
              <Field label="η_th"><Slider value={chpThEff} onChange={setChpThEff} min={0.35} max={0.6} step={0.01} postfix="-"/></Field>
            </div>
            <label className="sub" style={{display:'flex',alignItems:'center',gap:6}}>
              <input type="checkbox" checked={chpAuto} onChange={(e)=>setChpAuto(e.target.checked)} /> خودتنظیمی CHP
            </label>

            <div className="title" style={{marginTop:8}}>هاضم & ریکاوری</div>
            <div className="grid3">
              <Field label="VS پایه (kg/day)"><Slider value={digesterVSkgd_base} onChange={setDigesterVSkgd_base} min={0} max={1000} step={10} postfix="kg/day"/></Field>
              <Field label="بازده متان (m³/kgVS)"><Slider value={methaneYield} onChange={setMethaneYield} min={0.2} max={0.6} step={0.01} postfix="m³/kgVS"/></Field>
              <Field label="η مبدل حرارتی"><Slider value={recoverHXeta} onChange={setRecoverHXeta} min={0} max={0.9} step={0.05} postfix="-"/></Field>
            </div>

            <div className="title" style={{marginTop:8}}>گلخانه/آکواپونیک</div>
            <div className="grid3">
              <Field label="سطح گلخانه"><Slider value={ghArea} onChange={setGhArea} min={0} max={roofAreaMax} step={5} postfix="m²"/></Field>
              <Field label="تعداد طبقات"><Slider value={ghLayers} onChange={setGhLayers} min={1} max={8} step={1} postfix="layer"/></Field>
              <Field label="حجم استخر (m³)"><Slider value={tankVol_m3} onChange={setTankVol_m3} min={2} max={100} step={1} postfix="m³"/></Field>
            </div>
            <div className="grid3">
              <Field label="چگالی ماهی (kg/m³)"><Slider value={fishDensity_kgm3} onChange={setFishDensity_kgm3} min={5} max={60} step={1} postfix="kg/m³"/></Field>
              <Field label="FDR (%/day)"><Slider value={fdr_percent} onChange={setFdr_percent} min={0.5} max={3.0} step={0.1} postfix="%/day"/></Field>
              <Field label="ساعات نوردهی" hint="اگر خودکار خاموش است"><Slider value={lightHours} onChange={setLightHours} min={6} max={20} step={1} postfix="h"/></Field>
            </div>
            <div className="row" style={{gap:10,alignItems:'center',marginTop:6}}>
              <span className="sub">نور:</span>
              <button className="pill" style={{background: lightType==='LED'? '#2a3a78':'#17204a'}} onClick={()=>setLightType('LED')}>LED</button>
              <button className="pill" style={{background: lightType==='HPS'? '#2a3a78':'#17204a'}} onClick={()=>setLightType('HPS')}>HPS</button>
              <label className="sub" style={{display:'flex',alignItems:'center',gap:6}}>
                <input type="checkbox" checked={autoLighting} onChange={(e)=>setAutoLighting(e.target.checked)} /> نور خودکار
              </label>
            </div>

            <div className="title" style={{marginTop:8}}>آب</div>
            <div className="grid">
              <Field label="η آب خاکستری"><Slider value={greyEta} onChange={setGreyEta} min={0.3} max={0.95} step={0.05} postfix="-"/></Field>
              <Field label="ضریب رواناب باران"><Slider value={rainCoeff} onChange={setRainCoeff} min={0.2} max={0.95} step={0.05} postfix="-"/></Field>
            </div>
          </details>

          <div className="sep"/>

          <div className="kpi">
            <div className="card"><div className="lbl">برق PVT (روزانه)</div><div className="val">{sim.EelPVT_day_kWh.toFixed(1)} kWh</div></div>
            <div className="card"><div className="lbl">بیوگاز (kWh/d)</div><div className="val">{sim.biogas.E_fuel_biogas_kWh_day.toFixed(0)} kWh/d</div></div>
            <div className="card"><div className="lbl">تولید گیاهی</div><div className="val">{sim.Prod_kg_day.toFixed(1)} kg/d</div></div>
          </div>

        </div>

        {/* ستون راست: گراف و نتایج */}
        <div className="panel">
          <div className="title">گراف جریان‌ها و نتایج</div>

          <div className="svgwrap">
            <svg width="100%" height="100%" viewBox="0 0 960 560">
              {edges.map((e,i)=>(<Edge key={i} {...e}/>))}
              <NodeCard id="PVT" label="PVT" kpi={`P_el ${(sim.EelPVT_day_kWh/24).toFixed(1)} kW`} onClick={()=>{setShowModal('PVT'); setVarDetail(null);}}/>
              <NodeCard id="ABS" label="چیلر جذبی" kpi={`Q_cold ${((sim.QheatAvail_kW*0.7)||0).toFixed(1)} kW`} onClick={()=>{setShowModal('ABS'); setVarDetail(null);}}/>
              <NodeCard id="BLD" label="ساختمان" kpi={`شبکه ${sim.gridImport_kWh.toFixed(0)}↔${sim.gridExport_kWh.toFixed(0)} kWh`} onClick={()=>{setShowModal('BLD'); setVarDetail(null);}}/>
              <NodeCard id="GH"  label="گلخانه/آکواپونیک" kpi={`Prod ${sim.Prod_kg_day.toFixed(1)} kg/d`} onClick={()=>{setShowModal('AQUA'); setVarDetail(null);}}/>
              <NodeCard id="CHP" label="CHP" kpi={`P_el ${sim.chpElKw_auto.toFixed(1)} kW`} onClick={()=>{setShowModal('CHP'); setVarDetail(null);}}/>
              <NodeCard id="DIG" label="هاضم" kpi={`${sim.biogas.V_CH4_m3_day.toFixed(1)} m³ CH4/d`} onClick={()=>{setShowModal('DIG'); setVarDetail(null);}}/>
              <NodeCard id="WTR" label="آب/ریکاوری" kpi={`بازچرخ ${((sim.V_reuse_L_day+sim.V_rain_L_day)/1000).toFixed(2)} m³/d`} onClick={()=>{setShowModal('WATER'); setVarDetail(null);}}/>
            </svg>
          </div>

          <div className="legend">
            <div className="item"><span className="dot" style={{background:'var(--electric)'}}/> برق</div>
            <div className="item"><span className="dot" style={{background:'var(--heat)'}}/> حرارت</div>
            <div className="item"><span className="dot" style={{background:'var(--water)'}}/> آب</div>
            <div className="item"><span className="dot" style={{background:'var(--nutrient)'}}/> مواد مغذی/کود</div>
            <div className="item"><span className="dot" style={{background:'var(--co2)'}}/> سوخت/گاز</div>
          </div>

          <div className="sep"/>
          <div className="title">خروجی‌های کلیدی (روزانه)</div>
          <div className="kpi">
            <div className="card"><div className="lbl">میانگین تراز برق</div><div className="val">{(sim.PelBalance_auto.reduce((a,b)=>a+b,0)/24).toFixed(1)} kW</div></div>
            <div className="card"><div className="lbl">کمبود گرمایش</div><div className="val">{sim.QheatDef_kW.toFixed(1)} kW</div></div>
            <div className="card"><div className="lbl">آب خالص GH</div><div className="val">{(sim.W_net_L_day/1000).toFixed(2)} m³/d</div></div>
          </div>

          <div className="sep"/>
          <div className="grid">
            <SparkLine title="پروفایل تراز برق (kW)" series1={sim.PelBalance_auto} label1="ساعت‌به‌ساعت"/>
            <Bars title="آب (m³/day)" cats={["نیاز GH","بازچرخ","باران","کمبود"]} vals={[sim.W_grow_L_day/1000, sim.V_reuse_L_day/1000, sim.V_rain_L_day/1000, sim.W_net_L_day/1000]} />
          </div>
        </div>

      </div>

      {/* مودال نمایش فرمول‌ها و متغیرها */}
      <Modal open={!!showModal} onClose={()=>setShowModal(null)} title={`فرمول‌ها و متغیرها — ${showModal||''}`}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 360px',gap:12}}>
          <div>
            <div className="sub">فرمول‌های کلیدی ({showModal})</div>
            <div style={{marginTop:8}}>
              {(FORMULAS[showModal]||[]).map((it,i)=>(
                <div key={i} style={{marginBottom:10}}>
                  <div style={{fontFamily:'monospace',background:'#08102a',padding:8,borderRadius:6}}>{it.f}</div>
                  <div className="muted" style={{marginTop:6}}>{it.desc}</div>
                </div>
              ))}
            </div>

            <div className="sep"/>

            <div className="sub">لیست متغیرهای مرتبط — کلیک کنید تا وابستگی ببینید</div>
            <div className="var-list" style={{marginTop:8}}>
              {Object.entries(VARIABLES).slice(0,40).map(([k,v])=>(
                <div key={k} className="var-item" onClick={()=>setVarDetail(k)}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div><b>{k}</b> <span style={{fontSize:12,color:'#9fb4ff'}}>({v.unit})</span></div>
                    <div className="muted" style={{fontSize:12}}>{v.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{borderLeft:'1px solid rgba(255,255,255,0.03)',paddingLeft:12}}>
            <div className="sub">وابستگی متغیر</div>
            <div style={{marginTop:8}}>
              <VarDetailBox varId={varDetail} />
            </div>

            <div style={{marginTop:12}} className="sep"/>
            <div className="sub">مقادیر منتخب</div>
            <div style={{marginTop:8}} className="kpi">
              <div className="card"><div className="lbl">PVT (kWh/day)</div><div className="val">{sim.EelPVT_day_kWh.toFixed(1)}</div></div>
              <div className="card"><div className="lbl">بیوگاز (kWh/day)</div><div className="val">{sim.biogas.E_fuel_biogas_kWh_day.toFixed(0)}</div></div>
              <div className="card"><div className="lbl">تولید GH (kg/day)</div><div className="val">{sim.Prod_kg_day.toFixed(1)}</div></div>
            </div>
          </div>
        </div>
      </Modal>

    </div>
  );
}

/* ==================== کامپوننت‌های نموداری سبک ==================== */
function SparkLine({ title, series1, label1 }){
  const w=420, h=140, pad=20;
  const maxY = Math.max(1, ...series1.map(v=>Math.abs(v)));
  const path = series1.map((v,i)=>{
    const x = pad + i*((w-2*pad)/(series1.length-1));
    const y = h/2 - (v/maxY)*(h/2-10);
    return (i?"L":"M")+x+" "+y;
  }).join(" ");
  return (
    <div className="chart">
      <div className="sub" style={{margin:"4px 6px"}}>{title}</div>
      <svg width={w} height={h}>
        <line x1={pad} y1={h/2} x2={w-pad} y2={h/2} stroke="#2a3a78" strokeDasharray="4 4"/>
        <path d={path} stroke="var(--electric)" strokeWidth={2.5} fill="none"/>
      </svg>
      <div className="sub" style={{margin:"4px 6px"}}>{label1}</div>
    </div>
  );
}

function Bars({ title, cats, vals }){
  const w=420, h=140, pad=26;
  const maxY = Math.max(1,...vals);
  const bw = (w-2*pad)/vals.length - 10;
  return (
    <div className="chart">
      <div className="sub" style={{margin:"4px 6px"}}>{title}</div>
      <svg width={w} height={h}>
        {vals.map((v,i)=>{
          const x = pad + i*(bw+10);
          const hh = (v/maxY)*(h-40);
          const y = h-20-hh;
          return (
            <g key={i}>
              <rect x={x} y={y} width={bw} height={hh} fill={i===0? 'var(--water)': i===1? '#49c6e5': i===2? '#8ad6ff': '#f6ad55'} />
              <text x={x+bw/2} y={h-6} textAnchor="middle" fontSize="11" fill="#cfe2ff">{cats[i]}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
